<template>
	<div class="p4" style="height:100vh; overflow: scroll;">
		<!-- Clause selection -->
		<p class="xs-12 fs4 fw7 lh125">{{ $t("select_clause") }}</p>
		<j-base-multi-select-with-flexible-value
			class="mb6"
			v-model="selectedClause"
			:options="options"
			:allowMultiselect="false"
			:allowLocalSearch="true"
			placeholder="Search template..."
		/>

		<!-- Type selection -->
		<div class="flex xs-right mb6">
			<j-button
				class="mr1"
				size="xsmall"
				:type="type === 'content' ? 'secondary' : 'bordered'"
				@click="type = 'content'"
				>{{ $t("json_object") }}</j-button
			>
			<j-button
				size="xsmall"
				:type="type === 'variables' ? 'secondary' : 'bordered'"
				@click="type = 'variables'"
				>{{ $t("graphql_variables") }}</j-button
			>
		</div>

		<!-- Override -->
		<div v-if="selectedClause" class="flex xs-middle xs-right mb6">
			<j-base-toggle size="xsmall" v-model="isOverride" />
			<div class="fw7 ml2">{{ $t("override") }}</div>
		</div>

		<!-- contentJson -->
		<div
			v-show="!isOverride && type === 'content'"
			v-for="(item, key) in contentJson"
			:key="key"
			class="xs-middle mb3"
		>
			<!-- Label -->
			<div class="xs-12 fs4 fw4 mb1">{{ key }}</div>
			<!-- value -->
			<div class="xs-12 pr1">
				<core-form-input
					componentName="j-base-input-text"
					:name="`name-${key}`"
					size="small"
					:value="contentJson[key]"
					@input="handleInput(key, $event)"
				/>
			</div>
		</div>

		<!-- VariablesJson -->
		<div v-show="!isOverride && type === 'variables'">
			<p class="fs4 fw7 c1 mb1">{{ $t("graphql_variables") }}</p>
			<core-monaco-editor language="json" v-model="variablesJson" />
		</div>

		<!-- Override area -->
		<p v-if="isOverride" class="xs-12 fs4 fw7 lh125 mt4 mb2">HTML</p>
		<j-spinner v-if="isRendering" />
		<core-rich-text-editor
			v-if="isOverride && !isRendering"
			:content="htmlOverride"
			@input="updateHtml"
		/>

		<div class="flex xs-12 xs-right">
			<j-button class="mt2" @click="onAdd">{{ $t("add") }}</j-button>
		</div>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue"
import JSpinner from "@/components/JSpinner"
import CoreRichTextEditor from "@/modules/core/components/CoreRichTextEditor"
import JBaseToggle from "@/components/JBaseToggle"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"
import { Liquid } from "liquidjs"

export default {
	components: {
		JButton,
		CoreMonacoEditor,
		CoreFormInput,
		JBaseMultiSelectWithFlexibleValue,
		JBaseToggle,
		CoreRichTextEditor,
		JSpinner
	},

	props: {
		caseId: String,
		proposalId: String,
		offerId: String
	},

	data: () => ({
		type: "content", //content or variables
		selectedClause: null,
		contentJson: null,
		variablesJson: null,
		options: [],
		clauses: [],
		isOverride: false,
		htmlOverride: "",
		isRendering: false
	}),

	methods: {
		async fetchClauses() {
			const query = gql`
				query fetchClauseTemplates($where: templateWhereInput) {
					templates(where: $where) {
						list {
							id
							name
							... on clauseHtml {
								html
								order
							}
						}
					}
				}
			`
			const variables = {
				where: { type: "clauseHtml" }
			}
			const res = await api({ query, variables })
			this.options = res?.data?.templates?.list.map(item => ({
				id: item.id,
				name: item.name,
				order: item.order
			}))
			this.clauses = res?.data?.templates?.list
		},

		async onAdd() {
			const query = gql`
				mutation addClauseToProposalOffer(
					$caseId: String!
					$proposalId: String!
					$offerId: String!
					$input: addClauseInput!
				) {
					addClauseToProposalOffer(
						caseId: $caseId
						proposalId: $proposalId
						offerId: $offerId
						input: $input
					) {
						status
						errors
					}
				}
			`

			// Do not update offer if no selected clause
			if (!(this?.selectedClause && this.selectedClause?.length)) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: "No clause have been selected"
					}
				})
				return
			}

			let variables = {
				caseId: this.caseId,
				proposalId: this.proposalId,
				offerId: this.offerId,
				input: {
					order: this.selectedClause?.[0]?.order,
					templateId: this.selectedClause?.[0].id,
					...(this.isOverride && { htmlOverride: this.htmlOverride }),
					renderParameters: {
						name: "data",
						contentJsonString: JSON.stringify(this.contentJson),
						variablesJsonString: this.variablesJson
					}
				}
			}

			const res = await api({ query, variables })
			if (res?.data?.addClauseToProposalOffer?.status !== "success") {
				this.$store.dispatch(
					"showError",
					res?.data?.addClauseToProposalOffer?.errors?.[0] || [
						this.$t("errors.general")
					]
				)
				return
			} else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("clause_added")
					}
				})
			this.$emit("added")
		},

		parseHtmlToSchema(htmlString) {
			// declare liquidjs
			const liquidjs = new Liquid()
			/**
			 * parse htmlstring into ast
			 * filter out html reference in ast, only get items with liquid syntax
			 * strip "data." from string
			 * convert into object
			 */
			return liquidjs
				.parse(htmlString)
				?.filter(item => item.hasOwnProperty("value"))
				?.map(item => ({
					key: item?.value?.initial?.replace("data.", ""),
					value: item?.value?.filters
						?.find(obj => obj.name === "default")
						?.args?.[0].replace(/'|"/g, "")
				}))
				?.reduce((obj, item) => {
					return {
						...obj,
						[item.key]: item.value
					}
				}, {})
		},

		handleInput(key, $event) {
			if (this.checkIsNum($event)) this.contentJson[key] = parseInt($event)
			else this.contentJson[key] = $event
		},

		checkIsNum(value) {
			return /^\d+$/.test(value)
		},

		async renderClause() {
			const query = gql`
				query renderClause(
					$html: String!
					$input: templateRenderParametersInput!
				) {
					renderHtml(html: $html, input: $input) {
						errors
						status
						value
					}
				}
			`
			const variables = {
				html: this.computedHtml,
				input: {
					name: "data",
					contentJsonString: JSON.stringify(this.contentJson)
				}
			}
			this.isRendering = true
			const res = await api({ query, variables })
			this.isRendering = false

			this.htmlOverride = res?.data?.renderHtml?.value
		},

		updateHtml(input) {
			this.htmlOverride = input
		}
	},

	computed: {
		computedHtml() {
			if (this.selectedClause?.length > 0) {
				return this.clauses?.find(
					clause => clause.id === this.selectedClause?.[0]?.id
				)?.html
			} else return ""
		}
	},

	created() {
		this.fetchClauses()
	},

	watch: {
		selectedClause: {
			handler() {
				this.contentJson = this.parseHtmlToSchema(this.computedHtml)
			},
			deep: true
		},

		isOverride: {
			handler(newVal) {
				if (newVal) {
					this.renderClause()
				}
			}
		}
	}
}
</script>
